import type {
  AgentMessage,
  ChatProgressEvent,
  ChatStreamEvent,
  ChatTaskReference,
  ChatToolEvent,
} from '@/types/agent-chat';

export interface ChatStreamReducerContext {
  agentKey: string;
  agentMessageId: string;
  streamMessageId?: string;
  senderName: string;
  retryPayload: string;
  userMessageId?: string;
  receivedAt?: Date;
  filterText?: (value: string) => string;
}

const normalizeContent = (value: string): string =>
  value.replace(/\s+/g, ' ').trim();

const getMetadataString = (
  metadata: Record<string, unknown> | undefined,
  key: string
): string | undefined => {
  const value = metadata?.[key];
  return typeof value === 'string' ? value : undefined;
};

const getMetadataBoolean = (
  metadata: Record<string, unknown> | undefined,
  key: string
): boolean | undefined => {
  const value = metadata?.[key];
  return typeof value === 'boolean' ? value : undefined;
};

const getEventDate = (
  event: ChatStreamEvent,
  context: ChatStreamReducerContext
): Date => {
  if (event.createdAt) {
    const parsed = new Date(event.createdAt);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return context.receivedAt ?? new Date();
};

const getAssistantMessageKey = (
  event: ChatStreamEvent,
  context: ChatStreamReducerContext
): string =>
  event.messageId || context.streamMessageId || context.agentMessageId;

const matchesAssistantMessage = (
  message: AgentMessage,
  event: ChatStreamEvent,
  context: ChatStreamReducerContext
): boolean => {
  const eventMessageId = event.messageId;
  const streamMessageId = context.streamMessageId;

  return Boolean(
    (eventMessageId &&
      (message.messageId === eventMessageId ||
        message.id === eventMessageId)) ||
    (streamMessageId &&
      (message.messageId === streamMessageId ||
        message.id === streamMessageId)) ||
    message.id === context.agentMessageId
  );
};

const applyTextFilter = (
  value: string,
  context: ChatStreamReducerContext
): string => (context.filterText ? context.filterText(value) : value);

const getUserMessageIdFromEvent = (
  event: ChatStreamEvent
): string | undefined => getMetadataString(event.metadata, 'userMessageId');

const getProgressText = (event: ChatStreamEvent): string =>
  event.label || event.text || event.status || 'Working';

const updateUserMessageFromStream = (
  messages: AgentMessage[],
  event: ChatStreamEvent,
  context: ChatStreamReducerContext
): AgentMessage[] => {
  const backendUserMessageId = getUserMessageIdFromEvent(event);
  if (!backendUserMessageId || !context.userMessageId) return messages;

  return messages.map(message =>
    message.id === context.userMessageId
      ? {
          ...message,
          messageId: backendUserMessageId,
          conversationId: event.conversationId ?? message.conversationId,
          status: 'completed',
        }
      : message
  );
};

const ensureAssistantMessage = (
  messages: AgentMessage[],
  event: ChatStreamEvent,
  context: ChatStreamReducerContext
): AgentMessage[] => {
  const existing = messages.some(message =>
    matchesAssistantMessage(message, event, context)
  );

  if (existing) return messages;

  const createdAt = event.createdAt ?? context.receivedAt?.toISOString();

  return [
    ...messages,
    {
      id: getAssistantMessageKey(event, context),
      messageId: event.messageId,
      conversationId: event.conversationId,
      sender: context.agentKey || event.agentKey || '',
      content: '',
      timestamp: getEventDate(event, context),
      senderName: context.senderName,
      status: 'streaming',
      createdAt,
      metadata: event.metadata,
      isStreaming: true,
    },
  ];
};

const updateAssistantMessage = (
  messages: AgentMessage[],
  event: ChatStreamEvent,
  context: ChatStreamReducerContext,
  updater: (message: AgentMessage) => AgentMessage
): AgentMessage[] => {
  const withAssistant = ensureAssistantMessage(messages, event, context);

  return withAssistant.map(message =>
    matchesAssistantMessage(message, event, context)
      ? updater(message)
      : message
  );
};

const buildProgressEvent = (event: ChatStreamEvent): ChatProgressEvent => ({
  id:
    event.eventId ||
    `${event.type}-${event.status || event.label || event.createdAt || Date.now()}`,
  eventId: event.eventId,
  text: getProgressText(event),
  status: event.status,
  label: event.label,
  createdAt: event.createdAt,
  count: 1,
});

const appendProgressEvent = (
  progressEvents: ChatProgressEvent[] | undefined,
  event: ChatStreamEvent
): ChatProgressEvent[] => {
  const nextProgressEvent = buildProgressEvent(event);
  const existingEvents = progressEvents ?? [];
  const lastEvent = existingEvents.at(-1);

  if (
    lastEvent &&
    lastEvent.text === nextProgressEvent.text &&
    lastEvent.status === nextProgressEvent.status
  ) {
    return [
      ...existingEvents.slice(0, -1),
      {
        ...lastEvent,
        eventId: nextProgressEvent.eventId ?? lastEvent.eventId,
        createdAt: nextProgressEvent.createdAt ?? lastEvent.createdAt,
        count: (lastEvent.count ?? 1) + 1,
      },
    ];
  }

  return [...existingEvents, nextProgressEvent].slice(-8);
};

const buildToolEvent = (event: ChatStreamEvent): ChatToolEvent => ({
  id:
    event.eventId || event.toolName || `tool-${event.createdAt || Date.now()}`,
  eventId: event.eventId,
  toolName: event.toolName,
  label: event.label,
  status: event.status,
  provider: getMetadataString(event.metadata, 'provider'),
  effectType: getMetadataString(event.metadata, 'effectType'),
  success: getMetadataBoolean(event.metadata, 'success'),
  startedAt: event.type === 'tool_start' ? event.createdAt : undefined,
  completedAt: event.type === 'tool_result' ? event.createdAt : undefined,
  metadata: event.metadata,
});

const upsertToolEvent = (
  toolEvents: ChatToolEvent[] | undefined,
  event: ChatStreamEvent
): ChatToolEvent[] => {
  const existingEvents = toolEvents ?? [];
  const nextToolEvent = buildToolEvent(event);
  const matchingIndex = existingEvents.findIndex(toolEvent => {
    if (event.eventId && toolEvent.eventId === event.eventId) return true;
    if (!event.toolName || toolEvent.toolName !== event.toolName) return false;
    return toolEvent.status === 'running' || event.type === 'tool_start';
  });

  if (matchingIndex === -1) {
    return [...existingEvents, nextToolEvent];
  }

  return existingEvents.map((toolEvent, index) =>
    index === matchingIndex
      ? {
          ...toolEvent,
          ...nextToolEvent,
          id: toolEvent.id,
          startedAt: toolEvent.startedAt ?? nextToolEvent.startedAt,
        }
      : toolEvent
  );
};

const buildTaskReference = (event: ChatStreamEvent): ChatTaskReference => ({
  id: event.taskId || event.eventId || `task-${event.createdAt || Date.now()}`,
  taskId: event.taskId,
  label: event.label,
  title: getMetadataString(event.metadata, 'title'),
  status: event.status,
  createdAt: event.createdAt,
  metadata: event.metadata,
});

const upsertTaskReference = (
  taskReferences: ChatTaskReference[] | undefined,
  event: ChatStreamEvent
): ChatTaskReference[] => {
  const existingReferences = taskReferences ?? [];
  const nextTaskReference = buildTaskReference(event);

  if (
    existingReferences.some(
      reference =>
        reference.id === nextTaskReference.id ||
        (event.taskId && reference.taskId === event.taskId)
    )
  ) {
    return existingReferences.map(reference =>
      reference.id === nextTaskReference.id ||
      (event.taskId && reference.taskId === event.taskId)
        ? { ...reference, ...nextTaskReference }
        : reference
    );
  }

  return [...existingReferences, nextTaskReference];
};

export const applyChatStreamEventToMessages = (
  messages: AgentMessage[],
  event: ChatStreamEvent,
  context: ChatStreamReducerContext
): AgentMessage[] => {
  if (event.type === 'ping') return messages;

  const messagesWithUserUpdate = updateUserMessageFromStream(
    messages,
    event,
    context
  );

  switch (event.type) {
    case 'message_start':
      return updateAssistantMessage(
        messagesWithUserUpdate,
        event,
        context,
        message => ({
          ...message,
          messageId: event.messageId ?? message.messageId,
          conversationId: event.conversationId ?? message.conversationId,
          status: 'streaming',
          createdAt: event.createdAt ?? message.createdAt,
          metadata: event.metadata ?? message.metadata,
          isStreaming: true,
          progressText: message.progressText || 'Thinking',
        })
      );

    case 'progress':
      return updateAssistantMessage(
        messagesWithUserUpdate,
        event,
        context,
        message => ({
          ...message,
          progressText: getProgressText(event),
          progressEvents: appendProgressEvent(message.progressEvents, event),
          isStreaming: true,
        })
      );

    case 'delta':
      return updateAssistantMessage(
        messagesWithUserUpdate,
        event,
        context,
        message => ({
          ...message,
          content: applyTextFilter(
            `${message.content}${event.text ?? ''}`,
            context
          ),
          status: 'streaming',
          isStreaming: true,
        })
      );

    case 'tool_start':
      return updateAssistantMessage(
        messagesWithUserUpdate,
        event,
        context,
        message => ({
          ...message,
          progressText: event.label || message.progressText,
          progressEvents: appendProgressEvent(message.progressEvents, event),
          toolEvents: upsertToolEvent(message.toolEvents, event),
          isStreaming: true,
        })
      );

    case 'tool_result':
      return updateAssistantMessage(
        messagesWithUserUpdate,
        event,
        context,
        message => ({
          ...message,
          progressText: event.label || message.progressText,
          progressEvents: appendProgressEvent(message.progressEvents, event),
          toolEvents: upsertToolEvent(message.toolEvents, event),
          isStreaming: true,
        })
      );

    case 'task_created':
      return updateAssistantMessage(
        messagesWithUserUpdate,
        event,
        context,
        message => ({
          ...message,
          taskReferences: upsertTaskReference(message.taskReferences, event),
          isStreaming: true,
        })
      );

    case 'final':
      return updateAssistantMessage(
        messagesWithUserUpdate,
        event,
        context,
        message => ({
          ...message,
          content:
            event.text !== undefined
              ? applyTextFilter(event.text, context)
              : message.content,
          status: 'completed',
          completedAt: event.createdAt ?? message.completedAt,
          isStreaming: false,
          progressText: undefined,
          progressEvents: undefined,
          kind: 'normal',
          retry: undefined,
        })
      );

    case 'error':
      return updateAssistantMessage(
        messagesWithUserUpdate,
        event,
        context,
        message => ({
          ...message,
          content:
            event.text ||
            message.content ||
            'Unable to complete the chat response. Please try again.',
          status: 'failed',
          completedAt: event.createdAt ?? message.completedAt,
          isStreaming: false,
          progressText: undefined,
          progressEvents: undefined,
          kind: 'error',
          retry:
            event.metadata?.retryable === false
              ? undefined
              : {
                  label: 'Retry',
                  payload: context.retryPayload,
                },
        })
      );

    case 'done':
      if (
        !messagesWithUserUpdate.some(message =>
          matchesAssistantMessage(message, event, context)
        )
      ) {
        return messagesWithUserUpdate;
      }

      return updateAssistantMessage(
        messagesWithUserUpdate,
        event,
        context,
        message => ({
          ...message,
          isStreaming: false,
          progressText: undefined,
          progressEvents: undefined,
        })
      );

    default:
      return messagesWithUserUpdate;
  }
};

export const cancelPendingAssistantMessage = (
  messages: AgentMessage[],
  context: ChatStreamReducerContext
): AgentMessage[] =>
  messages.flatMap(message => {
    const isPendingAssistant =
      message.id === context.agentMessageId ||
      message.id === context.streamMessageId ||
      message.messageId === context.streamMessageId ||
      (message.messageId !== undefined &&
        message.messageId === context.agentMessageId);

    if (!isPendingAssistant) return [message];

    if (!message.content.trim()) return [];

    return [
      {
        ...message,
        status: 'cancelled',
        isStreaming: false,
        progressText: undefined,
        progressEvents: undefined,
      },
    ];
  });

export const isLikelyPersistedHistoryMessage = (
  localMessage: AgentMessage,
  historyMessage: AgentMessage
): boolean => {
  if (localMessage.kind === 'system' || localMessage.kind === 'error') {
    return false;
  }

  if (localMessage.status === 'cancelled' || localMessage.isStreaming) {
    return false;
  }

  if (!localMessage.conversationId && !localMessage.messageId) {
    return false;
  }

  if (localMessage.sender !== historyMessage.sender) return false;

  const localContent = normalizeContent(localMessage.content);
  const historyContent = normalizeContent(historyMessage.content);
  if (!localContent || localContent !== historyContent) return false;

  if (
    localMessage.conversationId &&
    historyMessage.conversationId &&
    localMessage.conversationId !== historyMessage.conversationId
  ) {
    return false;
  }

  const localTime = localMessage.timestamp.getTime();
  const historyTime = historyMessage.timestamp.getTime();
  const fiveMinutesMs = 5 * 60 * 1000;

  return Math.abs(localTime - historyTime) <= fiveMinutesMs;
};
